import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getNativeApps, refreshNativeApps, selectNativeApp, getAssignedCatalogue, getAssignedDestinations,
  executeAssignedApp, getAssignedOperation, getAssignedOperationByRequest, continueAssignedOperation, cancelAssignedOperation, exportAssignedOperation } from './assignedAppApi';
import { assignedAppMessage, assignedStageLabel, assignedOperationLabel } from './assignedAppPresentation';
import AssignedAppsPanel from './AssignedAppsPanel';
import NativeOperationControls, { downloadNativeOperationReport } from './NativeOperationControls';
import './NativeAppsPanel.css';

export default function NativeAppsPanel({ deviceId, name, onSaved }) {
  const [data, setData] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trainerBusy, setTrainerBusy] = useState(false);
  const [trainerDirty, setTrainerDirty] = useState(false);
  const [error, setError] = useState('');
  const [operation, setOperation] = useState(null);
  const [setup, setSetup] = useState(false);
  const generation = useRef(0);
  const inFlight = useRef(false);
  const storageKey = `native-tv-operation:${deviceId}`;
  const running = operation && ['pending', 'running'].includes(operation.status);
  const locked = loading || busy || running || trainerBusy || trainerDirty;
  const current = generation.current;
  const alive = useCallback((token) => generation.current === token, []);

  useLayoutEffect(() => {
    const token = ++generation.current;
    inFlight.current = false;
    setData(null); setChoices({}); setCatalogue([]); setLoading(true); setBusy(false);
    setError(''); setOperation(null); setSetup(false); setTrainerBusy(false); setTrainerDirty(false);
    Promise.all([getNativeApps(deviceId), getAssignedCatalogue()]).then(([result, apps]) => {
      if (alive(token)) { setData(result); setCatalogue(apps); }
    }).catch((failure) => { if (alive(token)) setError(assignedAppMessage(failure)); })
      .finally(() => { if (alive(token)) setLoading(false); });
    const requestId = sessionStorage.getItem(storageKey);
    if (requestId) {
      // An uncertain request stays recoverable. Never admit a second launch just
      // because its first status read failed during a reload.
      setOperation({ requestId, status: 'pending' });
      getAssignedOperationByRequest(deviceId, requestId).then((result) => { if (alive(token)) setOperation(result); })
        .catch((failure) => { if (alive(token)) setError(assignedAppMessage(failure, 'Recover the previous TV request before starting another.')); });
    }
    return () => { generation.current += 1; };
  }, [deviceId, storageKey, alive]);
  useEffect(() => {
    if (!running || !operation.id) return undefined;
    let active = true; let timer;
    const poll = async () => {
      try {
        const result = await getAssignedOperation(deviceId, operation.id);
        if (active) { setOperation(result); if (['pending', 'running'].includes(result.status)) timer = setTimeout(poll, 1000); }
      } catch (failure) { if (active) setError(assignedAppMessage(failure)); }
    };
    timer = setTimeout(poll, 1000);
    return () => { active = false; clearTimeout(timer); };
  }, [deviceId, operation?.id, running]);
  const reload = useCallback(async () => {
    const token = generation.current;
    const result = await getNativeApps(deviceId);
    if (alive(token)) setData(result);
  }, [deviceId, alive]);
  const run = async (work) => {
    if (inFlight.current || locked) return;
    const token = generation.current;
    inFlight.current = true; setBusy(true); setError('');
    try { await work(token); } catch (failure) { if (alive(token)) setError(assignedAppMessage(failure)); }
    finally { if (alive(token)) { inFlight.current = false; setBusy(false); } }
  };
  const save = (entry, profile) => run(async (token) => {
    const choice = choices[entry.nativeId] || {};
    await selectNativeApp(deviceId, { nativeId: entry.nativeId, enabled: choice.enabled ?? profile?.enabled ?? false,
      serviceKey: choice.serviceKey ?? profile?.profile.nativeApplication.serviceKey ?? '',
      ...(profile ? { profileId: profile.id, revision: profile.revision } : {}) });
    if (!alive(token)) return;
    await reload();
    if (alive(token)) {
      setChoices((previous) => { const next = { ...previous }; delete next[entry.nativeId]; return next; });
      await onSaved?.();
    }
  });
  const scan = (scope = 'launchable') => run(async (token) => {
    try { const result = await refreshNativeApps(deviceId, scope); if (alive(token)) setData(result); }
    catch (failure) {
      // The server retains failed-scan freshness. Display that retained state,
      // preserving local unsaved choices even if the scan itself was denied.
      if (alive(token)) { try { await reload(); } catch (_) { /* Keep the original scan failure. */ } }
      throw failure;
    }
  });
  const launch = (profile) => run(async (token) => {
    const destinations = await getAssignedDestinations(deviceId);
    if (!alive(token)) return;
    const destination = destinations.find((entry) => entry.profileId === profile.id && entry.id.split(':').length === 3);
    if (!destination) throw new Error('No app destination available. Refresh assignments.');
    const requestId = `native-launch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(storageKey, requestId);
    setOperation({ status: 'pending', requestId });
    try {
      const result = await executeAssignedApp(deviceId, destination.id, { requestId, profileRevision: profile.revision });
      if (alive(token)) setOperation(result);
    } catch (failure) {
      if (alive(token) && [400, 401, 403, 404, 409].includes(failure.response?.status)) { sessionStorage.removeItem(storageKey); setOperation(null); }
      throw failure;
    }
  });
  const recover = async () => {
    const token = generation.current;
    try { const result = await getAssignedOperationByRequest(deviceId, sessionStorage.getItem(storageKey)); if (alive(token)) { setOperation(result); setError(''); } }
    catch (failure) { if (alive(token)) setError(assignedAppMessage(failure)); }
  };
  const entries = (data?.inventory?.launchable?.apps || []).filter((entry) => entry.launchable !== false && entry.hidden !== true);
  const missing = (data?.profiles || []).filter((profile) => !entries.some((entry) => entry.nativeId === profile.profile.nativeApplication.nativeId));
  const patch = (id, value) => setChoices((previous) => ({ ...previous, [id]: { ...previous[id], ...value } }));
  return <section className="native-apps" aria-label={`Native apps on ${name || 'TV'}`} aria-busy={loading || busy}>
    <p>Manage apps on <strong>{name || 'this TV'}</strong>. Confirm each service match before saving. App discovery and launch do not validate channel control.</p>
    <div className="native-apps__toolbar">
      <button type="button" disabled={locked} onClick={() => scan()}>Scan TV apps</button>
      <button type="button" disabled={locked} onClick={() => run(reload)}>Refresh saved apps</button>
      <span role="status">Inventory: {data?.inventory?.launchable?.status || 'not collected'}</span>
    </div>
    {data?.inventory?.launchable?.collectedAt && <small>Last scan: {new Date(data.inventory.launchable.collectedAt).toLocaleString()}</small>}
    {loading ? <p role="status">Loading this TV’s apps…</p> : !entries.length && <p>No launchable apps reported yet. Pair the TV and scan its apps.</p>}
    {data?.inventory?.launchable?.status === 'unavailable' && <p className="native-apps__notice">The last scan failed. Previous observations and saved choices are retained; check the relay and scan again.</p>}
    {trainerDirty && <p>Save or close the current app settings before changing the managed app list.</p>}
    {operation && <div className={running ? 'native-apps__operation native-apps__operation--active' : 'native-apps__operation'}>
    {operation && <p role="status">{assignedOperationLabel(operation.status)} · {assignedStageLabel(operation.stage)} {operation.result?.message || assignedAppMessage(operation.message, '')}</p>}
    {running && <button type="button" onClick={recover}>Recover app request</button>}
    <NativeOperationControls key={`${deviceId}:${operation?.id}`} operation={operation} onContinue={() => continueAssignedOperation(deviceId, operation.id)}
      onCancel={() => cancelAssignedOperation(deviceId, operation.id)} onResult={(result) => { if (alive(current)) setOperation(result); }} onError={(failure) => { if (alive(current)) setError(assignedAppMessage(failure)); }}
      onExport={async () => downloadNativeOperationReport(await exportAssignedOperation(deviceId, operation.id), operation.id)} />
    </div>}
    <fieldset disabled={locked} className="native-apps__list">
      <legend>Choose apps to manage</legend>
      {entries.map((entry) => {
        const profile = data.profiles.find((item) => item.profile.nativeApplication.nativeId === entry.nativeId);
        const choice = choices[entry.nativeId] || {};
        const direct = !profile?.profile.assignedApp || profile.profile.assignedApp.preparation === 'direct_launch';
        const readiness = profile?.capabilityReadiness;
        return <div className="native-apps__card" key={entry.nativeId}>
          <label className="native-apps__choice"><input type="checkbox" checked={choice.enabled ?? profile?.enabled ?? false} onChange={(event) => patch(entry.nativeId, { enabled: event.target.checked })} /><strong>{entry.name}</strong></label>
          <small className="native-apps__identity">{entry.nativeId} · {entry.version || 'version unreported'}</small>
          <label>Service match<select value={choice.serviceKey ?? profile?.profile.nativeApplication.serviceKey ?? ''} onChange={(event) => patch(entry.nativeId, { serviceKey: event.target.value })}>
            <option value="">Custom / app launch only</option>{catalogue.map((app) => <option key={app.key} value={app.key}>{app.name}</option>)}
          </select></label>
          {!profile && entry.suggestedServices?.includes('fpt-play') && <p>Possible FPT Play — confirm the service match.</p>}
          {readiness && <dl className="native-apps__readiness">{[['Launch', readiness.launch], ['Preparation', readiness.preparation], ['Keypad', readiness.keypad], ['Guide', readiness.guide]].map(([label, ready]) => <div key={label}><dt>{label}</dt><dd>{ready ? label === 'Guide' ? 'Connected' : 'Operator confirmed' : 'Unverified'}</dd></div>)}</dl>}
          <div className="native-apps__toolbar">
            <button type="button" onClick={() => save(entry, profile)}>Save {entry.name}</button>
            {profile?.enabled && <button type="button" disabled={Boolean(choices[entry.nativeId]) || !direct && !readiness?.preparation} onClick={() => launch(profile)}>{direct ? 'Launch' : 'Prepare'} {entry.name}</button>}
          </div>
          {!direct && !readiness?.preparation && <small>Test and confirm this preparation in app routines before using it here.</small>}
        </div>;
      })}
      {missing.map((profile) => {
        const id = profile.profile.nativeApplication.nativeId;
        return <div className="native-apps__card" key={profile.id}>
          <label className="native-apps__choice"><input type="checkbox" checked={choices[id]?.enabled ?? profile.enabled} disabled={profile.enabled === false} onChange={(event) => patch(id, { enabled: event.target.checked })} />{profile.displayName}</label>
          <p>Unavailable in the saved launchable inventory. Configuration is retained; rescan before enabling it.</p>
          <button type="button" disabled={!choices[id]} onClick={() => save({ nativeId: id }, profile)}>Save retained choice</button>
        </div>;
      })}
    </fieldset>
    <details><summary>Hidden/system app diagnostics</summary>
      <button type="button" disabled={locked} onClick={() => scan('all')}>Read all-app diagnostics</button>
      <p>{data?.inventory?.all?.status || 'not collected'} · these entries are not automatically launchable.</p>
      <ul>{data?.inventory?.all?.apps?.slice(0, 100).map((entry) => <li key={entry.nativeId}>{entry.name} · {entry.nativeId} · {entry.version || 'version unreported'}</li>)}</ul>
      {data?.inventory?.all?.apps?.length > 100 && <p>Showing the first 100 of {data.inventory.all.apps.length} diagnostic entries.</p>}
    </details>

    {error && <p role="alert">{error}</p>}
    <button type="button" disabled={locked} onClick={() => setSetup((value) => !value)}>{setup ? 'Close app routines and channels' : 'Configure app routines and channels'}</button>
    {setup && <AssignedAppsPanel deviceId={deviceId} nativeConnector="lg_webos_remote" blocked={busy || running}
      onBusyChange={setTrainerBusy} onDirtyChange={setTrainerDirty} onChanged={reload} />}
    <p className="native-apps__notice">Unattended scheduling is unavailable pending separate validation.</p>
  </section>;
}
